import { History, Location } from 'history';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import Empty from 'antd/lib/empty';
import { Rule } from 'antd/lib/form';
import { TextAreaProps } from 'antd/lib/input/TextArea';

import { IMessageWrapperProps } from 'components/MessageWrapper/MessageWrapper';
import { EAccountSupplierFields } from 'types/settings/enums';
import { EAccountStatuses } from 'types/userTypes';
import { ErrorIcn } from 'utils/icons';

export const isDevelopment = process.env.NODE_ENV === 'development';
export const isProdEnv = process.env.REACT_APP_ENVIRONMENT === 'prod';
export const PLATFORM_CLIENT = process.env.REACT_APP_PLATFORM_CLIENT;
export const SUPPLIER_CLIENT = process.env.REACT_APP_PRIVATE_SUPPLIER_CLIENT;
export const SHOPIFY_APP_ID = process.env.REACT_APP_SHOPIFY_APP_ID;
// NOTE: COMET_CHAT_AUTH_KEY was removed — a full-access key must never live in the client bundle.
// App ID is public by design and is safe to keep here.
export const COMET_CHAT_APP_ID = process.env.REACT_APP_COMET_CHAT_APP_ID;
export const INTERCOM_APP_ID = process.env.REACT_APP_INTERCOM_APP_ID;

export const LINKS = {
  autoDs: {
    status: 'https://status.autods.com',
    importCsv: 'https://autods.s3-us-west-2.amazonaws.com/v2_bulk_upload_example.csv',
    map: 'https://help.autods.com/configure-your-trading-policies#map',
    tradingPolicy: 'https://help.autods.com/configure-your-trading-policies',
  },
};

export const questionMarks = {
  account: {
    establishmentDate: 'The date of establishment or founding of a company is the date on which that company chooses '
      + 'to claim as its starting point',
    discount: <>
      Add the percentage discount you wish to offer AutoDS members (a minimum of 20% is required).
      This discount will be automatically calculated for all your listed products. If you do not wish to offer
      an additional discount on your products, as you already offering wholesale prices on your site, check
      the
      next box: ‘I’m already offering wholesale prices on my site’.Important: We promote products based
      (among other factors) on the discount you offer our members. To change the discount, please contact
      support.<br />
      <a href={LINKS.autoDs.tradingPolicy} className="blue" target="_blank" rel="noopener noreferrer">Read more</a>
    </>,
    phoneNumber: 'We do not show your phone number to retailers. We need this so we can contact you if it is needed.',
    productsSource: 'Tell us where are you purchasing your products from. Please note you can select multiple sources.',
    sku: 'SKU is short for stock keeping unit which is used by retailers to identify and track its inventory or stock. '
      + 'An SKU is a unique code consisting of letters and numbers that identify characteristics of each product, '
      + 'such as manufacturer, brand, style, color, and size. Please insert the amount of unique SKU you own in your'
      + ' store. This is required for the initial application process, you do not need to update this filed in the '
      + 'future. Please note each product variation will have its own unique SKU.',
    webSite: 'Specify your store\'s marketing website to help us find you on the Internet. If you don\'t have one, '
      + 'please share your store\'s URL on the biggest eCom platform you sell.',
  },
  policies: {
    allowReturns: 'Activate this feature if you accept returns from your customers.',
    paysForShipping: 'Select the payee for the returned product shipping fee.',
    processingTime: 'The processing time is between when a customer places an order and when you fulfil it. '
      + 'Follow your policy to reduce complaints from customers. The shorter the processing time, the more likely '
      + 'our members will select your product.',
    restrictedMarketplaces: 'Exclude marketplaces where you do not wish your products to be published. '
      + 'We recommend keeping the restriction list small as possible so your products will get more exposure.',
    relatedToShipping: 'Add any additional information our members should know about your shipping policy. '
      + 'For example, preferred shipping carrier, packaging requirements etc.',
    returnsDescription: 'Add any additional information our members should know about your returns policy. '
      + 'For example, the condition of the returned product, the required packaging, specific carrier to be used etc.',
    shippingTime: 'Make sure to select the correct shipping time range for each location based on the shipping policy '
      + 'offered by your carrier',
    shippingPrice: 'The shipping price will be added to the selling price of each product.',
  },
  tradingPolicy: {
    map: <>
      After changing the default MAP, it won&apos;t be applied to already uploaded products. You can change it
      manually on the My Products page.<br />
      <a href={LINKS.autoDs.map} className="blue" target="_blank" rel="noopener noreferrer">Read more</a>
    </>,
  },
  store: {
    connect: 'Click here to connect your store to the AutoDS Suppliers Platform. You need to have a Shopify '
      + 'store containing all your products to list them on our platform.',
    description: 'Share more information about your store. Which items are you selling? Do you have a niche or '
      + 'specialty etc.',
    category: 'Categories will be assigned automatically, but if the product doesn’t match with our mapping, '
      + 'the system will use Default category',
    logo: 'Click ‘Add Image’ to upload your store logo',
    storeSync: 'If you uploaded new listings or made changes to existing ones, and wish them to be updated, click '
      + 'the Synchronize Products button',
    customizableBranding: 'Do you allow dropshippers to send you their logos for you to print on the packages of the '
      + 'orders you fulfill for them?',
    customizableBrandingTable: 'Use this logo to brand the packaging for this chosen order.',
  },
  payments: {
    moneyTransfer: 'The payment will be sent based on the frequency selected in the next field. For example, '
      + 'if you choose weekly payments with a minimum of $100 and you have less than $100, you will not receive '
      + 'a payment on that week. However, if you have a bigger amount to withdraw by the time the payment occurs, '
      + 'we\'ll send you the whole eligible amount.',
    otherAddress: 'Add additional information such as suite, apartment number or floor.',
    swiftBic: 'SWIFT stands for Society for Worldwide Interbank Financial Telecommunications system, '
      + 'and it was developed to facilitate transactions between banks and other financial institutions, '
      + 'allowing for money transfers to be quicker, more accurate, and secure. BIC stands for Bank Identification '
      + 'Code or Bank Identifier Code. This code is identical to SWIFT, which is why these terms are often used '
      + 'interchangeably. Make sure to insert valid information for a successful payment.',
    iban: 'Enter your International Bank Account Number. '
      + 'Make sure to insert valid information for a successful payment.',
    historicallyPaidOrders: 'This shows the total amount of money, that was historically sent to your bank account '
      + 'from AutoDS.',
    unpaidOrders: 'This shows the amount of money, that can be withdrawn in the next Payment Frequency when the '
      + 'Minimum Automatic Transfer Amount is reached. It consists of all orders, including refunds.',
    pending: 'This shows the amount of money that is being processed by our finance team and will be sent shortly '
      + 'to your bank account.',
  },
};

export const awsConfig = {
  region: process.env.REACT_APP_REGION,
  userPoolId: process.env.REACT_APP_USER_POOL_ID,
  userPoolWebClientId: process.env.REACT_APP_USER_POOL_WEB_CLIENT_ID,
  mandatorySignIn: false,
  oauth: {
    domain: process.env.REACT_APP_COGNITO_DOMAIN,
    scope: process.env.REACT_APP_SCOPE?.split(','),
    redirectSignIn: `${process.env.REACT_APP_PRIVATE_SUPPLIER_CLIENT}/social_callback`,
    redirectSignOut: `${process.env.REACT_APP_PRIVATE_SUPPLIER_CLIENT}/`,
    responseType: 'code',
  },
};

export const STORAGE_KEYS = {
  theme: 'autoDS.app.theme',
  rememberSession: 'autoDS.app.remember_session',
  userSelection: 'autoDS.app.user_selection',
  phoneNumber: 'autoDS.app.phoneNumber',
  leftPanel: 'autoDS.app.left_panel',
  productsLimit: 'autoDS.app.productsLimit',
  ordersLimit: 'autoDS.app.ordersLimit',
  modalProps: 'autoDS.app.modal_props',
  loginError: 'autoDS.app.loginError',
  ordersTable: 'autoDS.app.ordersTable',
  productsTable: 'autoDS.app.productsTable',
  fromShopifySocial: 'autoDS.app.from_shopify_social',
  isHiddenSubmissionLabel: 'autoDS.app.is_hidden_submission_label',
};

export const COOKIE_KEYS = {
  sessionSearch: 'session_search',
  idToken: 'suppliers.idToken',
  accessToken: 'suppliers.accessToken',
  refreshToken: 'suppliers.refreshToken',
};

interface IFormRules {
  [key: string]: Rule;
}

export interface IFC {
  history: History,
  location: Location,
}

export const notFoundContent = <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No results" />;

export const formRules: IFormRules = {
  required: { required: true, message: 'Field is required' },
  email: { type: 'string', pattern: /^[A-Z0-9+_.-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Must be valid' },
  onlyLatinLetters: {
    type: 'string',
    pattern: /^[A-Za-z]+$/,
    message: 'Can only contain Latin letters',
    whitespace: true,
  },
  onlyLatinLettersAndNumbers: {
    type: 'string',
    pattern: /^[A-Za-z0-9]+$/,
    message: 'Can only contain Latin letters and numbers',
    whitespace: true,
  },
  onlyLatinLettersAndSpace: {
    type: 'string',
    pattern: /^[A-Za-z- ]+$/,
    message: 'Can only contain Latin letters',
    whitespace: true,
  },
  withoutSpace: {
    type: 'string',
    pattern: /^[A-Za-z\d\S]+$/,
    whitespace: true,
    message: 'Can`t contain spaces',
  },
  latinNumberSpecs: {
    type: 'string',
    pattern: /^[\w\-?,.@!`'()+\/\[\]"<>{}$€£*%=#;:№ ]+$/g,
    whitespace: true,
    message: 'Can only contain Latin letters and numbers',
  },
  latinNumberSpecsEnter: {
    type: 'string',
    pattern: /^[\w\-?,.@!`'()+\/\[\]"<>{}$€£*%=#;:\n\s ]+$/g,
    whitespace: true,
    message: 'Can only contain Latin letters and numbers',
  },
  onlyNumbers: { pattern: /^[0-9]+$/, message: 'Must contains only numbers' },
  webSite: {
    pattern: /^(https:\/\/)?(www\.)?([a-zA-Z0-9]+(-?[a-zA-Z0-9])*\.)+[\w]{2,}(\/\S*)?$/ig,
    message: 'Must be valid',
  },
  whitespace: { type: 'string', whitespace: true, message: 'Can`t contain only spaces' },
  min3Char: { min: 3, message: 'Minimum 3 characters' },
  min5Char: { min: 5, message: 'Minimum 5 characters' },
  min8Char: { min: 8, message: 'Minimum 8 characters' },
  max15Char: { max: 15, message: 'Maximum 15 characters' },
  max20Char: { max: 15, message: 'Maximum 20 characters' },
  max25Char: { max: 25, message: 'Maximum 25 characters' },
  max30Char: { max: 30, message: 'Maximum 30 characters' },
  max36Char: { max: 36, message: 'Maximum 36 characters' },
  max50Char: { max: 50, message: 'Maximum 50 characters' },
};

export const passwordRules = [formRules.required, formRules.min8Char, formRules.max36Char, formRules.withoutSpace];

export const phoneNumberField = {
  label: 'Contact Phone Number',
  name: EAccountSupplierFields.PhoneNumber,
  component: PhoneInput,
  componentProps: { placeholder: 'Enter Contact Phone Number' },
  rules: [
    formRules.required, {
      validator(_: any, value: string) {
        if (isValidPhoneNumber(value || '')) {
          return Promise.resolve();
        }

        return Promise.reject(new Error('Must be valid'));
      },
    },
  ],
  tooltip: { title: questionMarks.account.phoneNumber },
};

export const momentFormats = {
  default: 'MMM DD, YYYY',
  withTime: 'MMM DD, YYYY, h:mm A',
  establishmentDate: 'YYYY-MM-DD',
};

const accountVerificationError = {
  isVisible: true,
  className: 'mb-3.5 h-10 bg-red-50 text-black-0',
  children: <><ErrorIcn className="text-black-0" />Account approval denied. Check your email for more information.</>,
};

export const accountVerificationStatuses: { [index: string]: IMessageWrapperProps } = {
  [EAccountStatuses.Pending]: {
    isVisible: true,
    className: 'mb-3.5 h-10 bg-blue-50 text-black-0',
    children: <>
      <ErrorIcn className="text-black-0" />
      Your account has not been approved yet. No worries! We will notify you by email when we finish reviewing your
      account.
    </>,
  },
  [EAccountStatuses.Declined]: accountVerificationError,
  [EAccountStatuses.FinalDeclined]: accountVerificationError,
};

export const activateProductsErrorByApprovalStatus = {
  [EAccountStatuses.Initial]: 'Please check your account status, your account is not approved yet',
  [EAccountStatuses.Pending]: 'You are not yet Approved by AutoDS Team, it can take up to '
  + '48 hours before getting final approval',
  [EAccountStatuses.Declined]: 'Please check your account status, your account is not approved yet',
  [EAccountStatuses.FinalDeclined]: 'Please check your account status, your account is not approved',
};

export const textareaWithCounterProps: Partial<TextAreaProps> = {
  allowClear: true,
  showCount: { formatter: ({ count, maxLength }) => `${count}/${maxLength} characters` },
};

export const multipleSelectProps = {
  mode: 'multiple',
  showArrow: true,
  optionFilterProp: 'children',
  notFoundContent,
};
